import { ArrowUpOutlined, StopOutlined } from "@ant-design/icons";
import { Input } from "antd";
import type { InputRef } from "antd/es/input";
import {
  forwardRef,
  KeyboardEvent,
  useImperativeHandle,
  useRef,
} from "react";

const { TextArea } = Input;

export interface ChatInputHandle {
  focus: () => void;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSend: (text: string) => void;
  onStop: () => void;
  loading: boolean;
  disabled?: boolean;
}

const ChatInput = forwardRef<ChatInputHandle, Props>(function ChatInput(
  { value, onChange, onSend, onStop, loading, disabled },
  ref
) {
  const innerRef = useRef<InputRef>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      innerRef.current?.focus();
    },
  }));

  function submit() {
    const text = value.trim();
    if (!text || loading || disabled) return;
    onSend(text);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  const canSend = value.trim() && !loading && !disabled;

  return (
    <div className="border-t border-slate-200 bg-white flex-shrink-0">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-4">
        <div className="relative border border-slate-200 rounded-xl bg-white focus-within:border-slate-400 transition-colors">
          <TextArea
            ref={innerRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Hỏi về khách hàng, chỉ số nước, hoá đơn..."
            autoSize={{ minRows: 1, maxRows: 6 }}
            disabled={disabled}
            variant="borderless"
            className="!text-base !py-3 !pl-4 !pr-14 !resize-none"
          />
          <div className="absolute right-2 bottom-2">
            {loading ? (
              <button
                type="button"
                onClick={onStop}
                className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 transition-colors flex items-center justify-center"
                aria-label="Dừng"
              >
                <StopOutlined />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={!canSend}
                className="w-9 h-9 rounded-lg text-white disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-400 transition-colors flex items-center justify-center"
                style={
                  canSend
                    ? { background: "var(--primary-color)" }
                    : undefined
                }
                aria-label="Gửi"
              >
                <ArrowUpOutlined />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 px-1">
          <p className="text-xs text-slate-400">
            Enter để gửi, Shift+Enter xuống dòng — chọn gợi ý phía trên để điền sẵn
          </p>
          <p className="text-xs text-slate-400">Made by Quản Trị Số</p>
        </div>
      </div>
    </div>
  );
});

export default ChatInput;
